import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./CtaCorriente.css";
import CtaCorrienteList from "../../components/ctaCorrienteList/CtaCorrienteList.js";
import DropdownSelect from "../../components/dropdownSelect/DropdownSelect.js";
import {
  getCuentasCorrientesByNroCta,
  deleteAndGetCuentasCorrientesByNroCtaFromSoftland,
} from "../../api/cuentaCorriente.js";
import SelectList from "../../components/SelectList/SelectList.js";

//Util
import { getDateStringYYYYMMDD } from "../../utils/formatDate.js";

//Contexts
import authContext from "../../context/authentication/authContex.js";
import proveedoresContext from "../../context/proveedores/proveedoresContext.js";

// Alertas
import Swal from "sweetalert2";

//Permisos
import { ACTUALIZAR_MOVIMIENTOS_PROVEEDOR } from "../../utils/permisos";

const CtaCorriente = () => {
  const [filtros, setFiltros] = useState({
    fchdes: "1980-01-01",
    fchhas: getDateStringYYYYMMDD(),
  });
  const [orderBy, setOrderBy] = useState("Fecha");
  const optionsOrder = [
    { label: "Fecha", value: "date" },
    { label: "Titulo", value: "title" },
  ];
  const [order, setOrder] = useState("desc");
  const [list, setList] = useState([]);
  const [provider, setProvider] = useState({});
  const [empresa, setEmpresa] = useState(null);
  const ascCheck = useRef("");
  const descCheck = useRef("");

  const aContext = useContext(authContext);
  const { usuario, globalSettings } = aContext;

  const provContext = useContext(proveedoresContext);
  const { proveedores, actualizarProveedores } = provContext;

  const isAdminOrControl = usuario.userType.some((item) =>
    ACTUALIZAR_MOVIMIENTOS_PROVEEDOR.includes(item.typeName)
  );
  const isProvUser = usuario.userType.some((item) => item.typeName === "PROV");

  useEffect(() => {
    actualizarProveedores();
  }, []);

  const empresasOptions = useMemo(() => {
    const receptoras = globalSettings?.empresasReceptoras || [];
    if (isAdminOrControl) {
      return receptoras.map((e) => ({ value: e.companyName, label: e.razonSocial }));
    }
    if (isProvUser) {
      const companyNamesDelProv = new Set(
        proveedores
          .filter((p) => p.cuit === usuario.cuit && p.companyName)
          .map((p) => p.companyName)
      );
      return receptoras
        .filter((e) => companyNamesDelProv.has(e.companyName))
        .map((e) => ({ value: e.companyName, label: e.razonSocial }));
    }
    return [];
  }, [globalSettings, proveedores, isAdminOrControl, isProvUser, usuario.cuit]);

  const proveedoresFiltrados = useMemo(() => {
    if (!empresa) return proveedores;
    return proveedores.filter((p) => p.companyName === empresa);
  }, [proveedores, empresa]);

  const handleChangeEmpresa = async (value) => {
    const selectedEmpresa = value.value;
    setEmpresa(selectedEmpresa);
    setProvider({});
    setList([]);

    if (isProvUser) {
      const matchingProvider = proveedores.find(
        (p) => p.cuit === usuario.cuit && p.companyName === selectedEmpresa
      );
      if (matchingProvider) {
        setProvider(matchingProvider);
        const resp = await getCuentasCorrientesByNroCta(matchingProvider.nroCta, matchingProvider.companyName);
        setList(resp.data.data);
      }
    }
  };

  const actualizarMovimientosSegunProveedor = async () => {
    Swal.fire({
      title: `Desea actualizar los movimientos del proveedor ${provider.nroCta} - ${provider.nombre}?`,
      text: "Se traeran los datos de Softland para sobreescribir los movimientos de cuenta corriente del proveedor seleccionado.",
      showCancelButton: true,
      confirmButtonText: "Actualizar",
      cancelButtonText: "No, cancelar",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        return await deleteAndGetCuentasCorrientesByNroCtaFromSoftland(
          provider.nroCta,
          provider.companyName
        )
          .then((response) => {
            if (!response.status) {
              throw new Error(response.msg);
            }
            return response;
          })
          .catch((error) => {
            Swal.showValidationMessage(`Request failed: ${error}`);
          });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        setList(result.value.data.data);
        Swal.fire(
          "Actualizado!",
          "La base de datos ha sido actualizada correctamente.",
          "success"
        );
      }
    });
  };

  const handleChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleClickCheck = (e) => {
    if (e.target.name == "descOrder") {
      if (ascCheck.current.checked) ascCheck.current.checked = false;
      setOrder("desc");
    } else {
      if (descCheck.current.checked) descCheck.current.checked = false;
      setOrder("asc");
    }
  };

  const handleChangeProveedor = async (value) => {
    const proveedorSeleccionado = proveedoresFiltrados.find(
      (p) =>
        `${p.nroCta}`.replace(/\s+/g, "") ===
        value.value.split(" ~ ")[0].replace(/\s+/g, "")
    );

    if (proveedorSeleccionado !== undefined) {
      setProvider(proveedorSeleccionado);
    }

    const resp = await getCuentasCorrientesByNroCta(
      proveedorSeleccionado.nroCta,
      proveedorSeleccionado.companyName
    );
    setList(resp.data.data);
  };

  const selectorStyles = {
    additionalStyleDivSelectList: {
      alignItems: "center",
      gap: "0.5rem",
    },
    fontStyleTitle: {
      fontSize: "1.5rem",
    },
  };

  return (
    <div className="ctaCorrienteDiv">
      <div className="asideFiltros">
        <SelectList
          text={"Empresa:"}
          options={empresasOptions}
          handleChange={handleChangeEmpresa}
          property={"Empresa"}
          valueAux={{
            label: empresa
              ? (globalSettings?.empresasReceptoras || []).find((e) => e.companyName === empresa)?.razonSocial || empresa
              : "Seleccione...",
            value: empresa || "",
          }}
          emptyInputs={[]}
          styles={selectorStyles}
        />
        <SelectList
          text={"Proveedor:"}
          options={proveedoresFiltrados.map((p) => ({
            value: `${p.nroCta}`,
            label: `${p.nroCta} - ${p.nombre}`,
          }))}
          handleChange={handleChangeProveedor}
          property={"Proveedor"}
          valueAux={{
            label:
              provider.nombre && provider.nroCta
                ? `${provider.nroCta} - ${provider.nombre}`
                : "Seleccione...",
            value: provider.nroCta || "",
          }}
          emptyInputs={[]}
          disabled={isProvUser}
          styles={selectorStyles}
        />
        <div className="inputGroup">
          <label>Fecha desde:</label>
          <input
            name="fchdes"
            type="date"
            defaultValue="1980-01-01"
            onChange={(e) => handleChange(e)}
          ></input>
        </div>
        <div className="inputGroup">
          <label>Fecha hasta:</label>
          <input
            name="fchhas"
            type="date"
            defaultValue={getDateStringYYYYMMDD()}
            onChange={(e) => handleChange(e)}
          ></input>
        </div>
        <div className="inputGroup">
          <label>Ordenar por...</label>
          <DropdownSelect
            options={optionsOrder}
            setFilter={setOrderBy}
            onlySelectFunction={true}
            placeholder="Fecha"
          />
          <div className="setOrderDiv">
            <div className="inputGroup">
              <label>Asc</label>
              <input
                name="ascOrder"
                ref={ascCheck}
                type="checkbox"
                onClick={(e) => handleClickCheck(e)}
              />
            </div>
            <div className="inputGroup">
              <label>Desc</label>
              <input
                name="descOrder"
                defaultChecked
                ref={descCheck}
                type="checkbox"
                onClick={(e) => handleClickCheck(e)}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="resultadosDiv">
        {isAdminOrControl && (
          <button
            className={`actualizar-providers-btn ${
              !empresa || !provider?.nroCta ? "disabled-btn" : ""
            }`}
            style={{
              marginLeft: "auto",
              marginTop: "-55px",
              display: "block",
            }}
            disabled={!empresa || !provider?.nroCta}
            onClick={actualizarMovimientosSegunProveedor}
          >
            Actualizar Movimientos
          </button>
        )}
        <CtaCorrienteList
          fchdes={filtros.fchdes}
          fchhas={filtros.fchhas}
          orderBy={orderBy}
          order={order}
          list={list}
        />
      </div>
    </div>
  );
};

export default CtaCorriente;
